import { CheckIcon } from '@langchain/untitled-ui-icons';

import { Text } from '@/design-system';
import { useCurrentOrganizationBilling } from '@/hooks/useSwr';
import { cn } from '@/utils/tailwind';

import { isLowerTier } from '../utils/helpers';
import { planToDisplayName } from '../utils/planToDisplayName';
import { PaymentPlanCardActionButton } from './PaymentPlanCardActionButton';
import { PaymentPlanStatusBadge } from './PaymentPlanStatusBadge';
import { useShowPlanStatusBadge } from './hooks/useShowPlanStatusBadge';
import { PaymentPlanCardTiers } from './types';
import { PLAN_INFOS } from './utils/getPlanInfo';

type Props = {
  cardPlanType: PaymentPlanCardTiers;
};

export const PaymentPlanCard = ({ cardPlanType }: Props) => {
  const planInfo = PLAN_INFOS[cardPlanType];

  const { planPriceHead, planPriceDesc, planDesc, planFeatures } = planInfo;
  const currentOrganization = useCurrentOrganizationBilling();
  const currentPlan = currentOrganization.data?.current_plan;

  const {
    showStatusBadge,
    isCurrentPlan,
    isUpcomingPlan,
    isApprovedPlan,
    hasCancelled,
    upcomingPlanStartedOn,
    endOfBillingPeriodFormatted,
  } = useShowPlanStatusBadge({ cardPlanType });

  return (
    <div
      className={cn(
        'flex max-w-[290px] flex-col self-stretch rounded-[20px]',
        showStatusBadge && 'bg-purple',
        hasCancelled && 'bg-error-primary'
      )}
    >
      <PaymentPlanStatusBadge
        currentPlan={currentPlan}
        showStatusBadge={showStatusBadge}
        isCurrentPlan={isCurrentPlan}
        isUpcomingPlan={isUpcomingPlan}
        isApprovedPlan={isApprovedPlan}
        hasCancelled={hasCancelled}
        upcomingPlanStartedOn={upcomingPlanStartedOn}
        endOfBillingPeriodFormatted={endOfBillingPeriodFormatted}
      />

      <div
        className={cn(
          'flex h-full grow flex-col gap-3 rounded-[20px] border border-secondary bg-primary px-7 py-8',
          'shadow-[4px_4px_4px_0_rgba(42,44,60,0.02),2px_5px_38px_0_rgba(42,44,60,0.05)]',
          'overflow-x-hidden',
          !showStatusBadge && 'mt-7',
          isLowerTier(cardPlanType, currentPlan?.tier) &&
            'border-none bg-secondary'
        )}
      >
        <div className="flex h-[150px] flex-col items-start justify-start gap-2 self-stretch">
          <Text variant="h3" className="capitalize" weight="normal">
            {planToDisplayName(cardPlanType)}
          </Text>
          <div className="flex items-end gap-2">
            <Text variant="h1" weight="medium" className="text-3xl">
              {planPriceHead}
            </Text>
            <Text variant="md" weight="medium" className="pb-1">
              {planPriceDesc}
            </Text>
          </div>
          <Text color="quaternary" weight="normal" className="overflow-hidden">
            {planDesc}
          </Text>
        </div>
        <div className="flex flex-col gap-6">
          <PaymentPlanCardActionButton cardPlanType={cardPlanType} />
          <div className="flex flex-col items-start justify-start gap-4">
            {planFeatures.map((f, index) => {
              return (
                <div
                  className={cn(
                    'flex items-start gap-2',
                    isLowerTier(cardPlanType, currentPlan?.tier) &&
                      'text-quaternary'
                  )}
                  key={typeof f === 'string' ? f : index}
                >
                  <CheckIcon className="mt-[2px] h-4 w-4 flex-none text-quaternary" />
                  {typeof f === 'string' ? <Text>{f}</Text> : f}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPlanCard;
